/**
 * Web Storage Adapter
 * Implements IStorageAdapter using browser localStorage with in-memory fallback.
 */

import { IStorageAdapter } from './storageAdapter';

class MemoryStorage implements IStorageAdapter {
  private store: Map<string, string> = new Map();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  clear(): void {
    this.store.clear();
  }
}

class WebStorageAdapter implements IStorageAdapter {
  private fallback: MemoryStorage = new MemoryStorage();

  private isAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && 'localStorage' in window && window.localStorage !== null;
    } catch {
      return false;
    }
  }

  getItem(key: string): string | null {
    if (this.isAvailable()) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return this.fallback.getItem(key);
      }
    }
    return this.fallback.getItem(key);
  }

  setItem(key: string, value: string): void {
    if (this.isAvailable()) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch {
        this.fallback.setItem(key, value);
        return;
      }
    }
    this.fallback.setItem(key, value);
  }

  removeItem(key: string): void {
    if (this.isAvailable()) {
      try {
        window.localStorage.removeItem(key);
        return;
      } catch {
        this.fallback.removeItem(key);
        return;
      }
    }
    this.fallback.removeItem(key);
  }

  clear(): void {
    if (this.isAvailable()) {
      try {
        window.localStorage.clear();
        return;
      } catch {
        this.fallback.clear();
        return;
      }
    }
    this.fallback.clear();
  }
}

export const webStorage = new WebStorageAdapter();
